function debounce(fn, wait) {
  let t;
  return function(...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

const dispatchCustomEvent = (eventName, data = {}) => {
  document.dispatchEvent(new CustomEvent(eventName, { detail: data }));
};

window.recentlyViewedIds = [];

if (!customElements.get('quantity-selector')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.input = this.querySelector('.qty');
      this.step = parseFloat(this.input.getAttribute('step')) || 1;
      this.changeEvent = new Event('change', { bubbles: true });

      this.subtract = this.querySelector('.minus');
      this.add = this.querySelector('.plus');

      this.subtract.addEventListener('click', () => this.changeQuantity(-1 * this.step));
      this.add.addEventListener('click', () => this.changeQuantity(1 * this.step));

      this.classList.add('buttons_added');
      this.validateQtyRules();
    }

    changeQuantity(change) {
      let quantity = parseInt(this.input.value) || 0;
      const newValue = Math.max(quantity + change, 1);
      const min = parseInt(this.input.min) || 1;
      const max = parseInt(this.input.max) || Infinity;
      if (newValue < min || newValue > max) return;

      this.input.value = newValue;
      this.input.dispatchEvent(this.changeEvent);
      this.validateQtyRules();
    }

    validateQtyRules() {
      const value = parseInt(this.input.value) || 0;
      const min = parseInt(this.input.min) || 1;
      const max = parseInt(this.input.max) || Infinity;

      this.subtract.classList.toggle('disabled', value <= min);
      this.add.classList.toggle('disabled', value >= max);
    }
  }

  customElements.define('quantity-selector', QuantityInput);
}

/**
 *  @class
 *  @function ProductCard
 */
if (!customElements.get('product-card')) {
  class ProductCard extends HTMLElement {

    constructor() {
      super();
    }

    connectedCallback() {
      this.swatches = this.querySelector('.product-card-swatches');
      this.image = this.querySelector('.product-card-image-link .product-primary-image');
      this.additional_images = this.querySelectorAll('.product-secondary-image');
      this.additional_images_nav = this.querySelectorAll('.product-secondary-images-nav li');
      this.quick_add = this.querySelector('.product-card--add-to-cart-button-simple');

      if (this.swatches) {
        this.enableSwatches();
      }
      if (this.additional_images) {
        this.enableAdditionalImages();
      }
      if (this.quick_add) {
        this.enableQuickAdd();
      }
    }

    enableAdditionalImages() {
      const image_length = this.additional_images.length;
      const images = this.additional_images;
      const nav = this.additional_images_nav;
      const image_container = this.querySelector('.product-card-image');

      if (!image_length || !image_container) return;

      const mousemove = function(e) {
        const rect = this.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const l = clientX - rect.left;
        const w = rect.width;

        if (!w) return;

        let sel = Math.floor((l / w) * image_length);
        sel = Math.max(0, Math.min(sel, image_length - 1));

        images.forEach((image, index) => {
          image.classList.remove('hover');
          if (nav[index]) nav[index].classList.remove('active');
        });

        const selimg = images[sel];
        if (selimg) {
          selimg.classList.add('hover');
          if (nav[sel]) nav[sel].classList.add('active');
        }
      };

      const mouseleave = function() {
        images.forEach((image, index) => {
          image.classList.remove('hover');
          if (nav[index]) nav[index].classList.remove('active');
        });
      };

      image_container.addEventListener('touchstart', mousemove, { passive: true });
      image_container.addEventListener('touchmove', mousemove, { passive: true });
      image_container.addEventListener('touchend', mouseleave, { passive: true });
      image_container.addEventListener('mouseenter', mousemove, { passive: true });
      image_container.addEventListener('mousemove', mousemove, { passive: true });
      image_container.addEventListener('mouseleave', mouseleave, { passive: true });

      window.addEventListener('load', () => {
        images.forEach((image) => {
          if (window.lazySizes?.loader) {
            window.lazySizes.loader.unveil(image);
          }
        });
      });
    }

    enableSwatches() {
      let swatch_list = this.swatches.querySelectorAll('.product-card-swatch');
      this.color_index = this.swatches.dataset.index;
      this.org_srcset = this.image ? this.image.dataset.srcset : '';

      this.addEventListener('change', this.onSwatchChange.bind(this));

      swatch_list.forEach((swatch) => {
        window.addEventListener('load', () => {
          if (!swatch.dataset.srcset || !window.lazySizes?.loader) return;
          const image = new Image();
          image.srcset = swatch.dataset.srcset;
          window.lazySizes.loader.unveil(image);
        });
        swatch.addEventListener('click', this.onSwatchClick);
        swatch.addEventListener('mouseover', this.onSwatchHover);
      });
    }

    onSwatchClick() {
      window.location.href = this.dataset.href;
    }

    onSwatchHover() {
      const swatch_input = this.previousElementSibling;
      if (!swatch_input) return;
      swatch_input.checked = true;
      swatch_input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onSwatchChange(evt) {
      if (this.image) {
        if (evt.target.dataset.srcset) {
          this.image.setAttribute('srcset', evt.target.dataset.srcset);
        } else {
          this.image.setAttribute('srcset', this.org_srcset);
        }
      }
    }

    enableQuickAdd() {
  if (this._quickAddEnabled) return;   // prevent duplicate listeners on re-connect
  this._quickAddEnabled = true;
  this.quick_add.addEventListener("click", this.quickAdd.bind(this));
    }

    quickAdd(evt) {
      evt.preventDefault();
      if (this.quick_add.disabled) return;

      this.quick_add.classList.add('loading');
      this.quick_add.setAttribute('aria-disabled', true);

      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        }
      };

      let formData = new FormData();
      formData.append('id', this.quick_add.dataset.productId);
      formData.append('quantity', 1);
      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);

      // Pass club price as a line item property if set on the button
      const clubPrice = this.quick_add.dataset.clubPrice;
      if (clubPrice) {
        formData.append('properties[_powerx_custom_price]', clubPrice);
      }

      config.body = formData;

      fetch(`${theme.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            dispatchCustomEvent('cart:error', {
              message: response.description || response.message || 'Could not add item to cart.'
            });
            return;
          }
          this.renderContents(response);
          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
        })
        .catch((e) => {
          console.error(e);
          dispatchCustomEvent('cart:error', { message: 'Network error. Please try again.' });
        })
        .finally(() => {
          this.quick_add.classList.remove('loading');
          this.quick_add.removeAttribute('aria-disabled');
        });

      return false;
    }

    getSectionsToRender() {
      return [
        { id: 'Cart',              section: 'main-cart',    selector: '.thb-cart-form' },
        { id: 'Cart-Drawer',       section: 'cart-drawer',  selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-secondary-area--item-count' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-secondary-area--item-amount' }
      ];
    }

    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section) => {
        if (!document.getElementById(section.id)) return;
        const elementToReplace =
          document.getElementById(section.id).querySelector(section.selector) ||
          document.getElementById(section.id);
        if (!parsedState.sections?.[section.section]) return;
        elementToReplace.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      });

      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
        cartDrawer.removeProductEvent();
      }
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      if (!html) return '';
      const selected = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector);
      return selected ? selected.innerHTML : '';
    }
  }
  customElements.define('product-card', ProductCard);
}

/**
 *  @class
 *  @function PanelClose
 */
if (!customElements.get('side-panel-close')) {
  class PanelClose extends HTMLElement {

    constructor() {
      super();
    }

    connectedCallback() {
      const cc = document.querySelector('.click-capture');

      this.addEventListener('click', (e) => this.close_panel(e));

      document.addEventListener('panel:close', (e) => {
        const panel = document.querySelector('.side-panel.active');
        if (panel) this.close_panel(e, panel);
      });

      if (cc) {
        cc.addEventListener('click', (e) => {
          const panel = document.querySelector('.side-panel.active');
          if (panel) this.close_panel(e, panel);
        });
      }
    }

    close_panel(e, panel) {
      if (e) e.preventDefault();
      if (!panel) {
        panel = e?.target.closest('.side-panel');
        if (!panel) return;
      }
      panel.classList.remove('active');
      document.body.classList.remove('open-cc');
      panel.setAttribute('inert', '');
    }
  }
  customElements.define('side-panel-close', PanelClose);

  document.addEventListener('keyup', (e) => {
    if (e.code && e.code.toUpperCase() === 'ESCAPE') {
      dispatchCustomEvent('panel:close');
    }
  });
}

/**
 *  @class
 *  @function CartDrawer
 */
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {

    constructor() {
      super();
    }

    connectedCallback() {
      this.isOpen = false;
      this.button = document.getElementById('cart-drawer-toggle');

      this._keyupController = new AbortController();

      this.debouncedOnChange = debounce((event) => {
        this.onChange(event);
      }, 300);

      document.addEventListener('cart:refresh', () => this.refresh());
      this.addEventListener('change', this.debouncedOnChange.bind(this));
      this.removeProductEvent();
      document.addEventListener('click', this.onWindowClick.bind(this));

      if (this.button) {
        this.button.addEventListener('click', (e) => {
          e.preventDefault();
          this.isOpen ? this.close() : this.open();
        });
      }

      document.addEventListener('keyup', (e) => {
        if (e.code && e.code.toUpperCase() === 'ESCAPE') {
          this.close();
        }
      }, { signal: this._keyupController.signal });
    }

    disconnectedCallback() {
      this._keyupController?.abort();
    }

    onWindowClick(event) {
      if (!this.contains(event.target) && this.isOpen && (!this.button || !this.button.contains(event.target))) {
        this.close();
      }
    }

    open() {
      this.removeAttribute('inert');
      this.focus();
      this.isOpen = true;
      dispatchCustomEvent('cart-drawer:open');
    }

    close() {
      this.setAttribute('inert', '');
      this.isOpen = false;
    }

    onChange(event) {
      if (event.target.classList.contains('qty')) {
        this.updateQuantity(event.target.dataset.index, event.target.value);
      }
    }

    removeProductEvent() {
      let removes = this.querySelectorAll('.remove');
      removes.forEach((remove) => {
        remove.addEventListener('click', (event) => {
          this.updateQuantity(event.target.dataset.index, '0');
          event.preventDefault();
        });
      });
    }

    getSectionsToRender() {
      return [
        { id: 'Cart-Drawer',        section: 'cart-drawer',  selector: '.cart-drawer--inner' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble',  selector: '.thb-secondary-area--item-count' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble',  selector: '.thb-secondary-area--item-amount' }
      ];
    }

    getSectionInnerHTML(html, selector) {
      if (!html) return '';
      const selected = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector);
      return selected ? selected.innerHTML : '';
    }

    updateQuantity(line, quantity) {
      this.querySelector(`#CartDrawerItem-${line}`)?.classList.add('thb-loading');

      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      });

      dispatchCustomEvent('line-item:change:start', { quantity });

      fetch(`${theme.routes.cart_change_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body
      })
        .then((response) => response.text())
        .then((state) => {
          const parsedState = JSON.parse(state);

          this.getSectionsToRender().forEach((section) => {
            const el =
              document.getElementById(section.id)?.querySelector(section.selector) ||
              document.getElementById(section.id);
            if (el && parsedState.sections) {
              el.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector
              );
            }
          });

          this.removeProductEvent();
          dispatchCustomEvent('line-item:change:end', { quantity, cart: parsedState });
          this.querySelector(`#CartDrawerItem-${line}`)?.classList.remove('thb-loading');
        })
        .catch((err) => {
          console.error('Cart update failed:', err);
          // Clear loading state so the item doesn't appear stuck
          this.querySelector(`#CartDrawerItem-${line}`)?.classList.remove('thb-loading');
          dispatchCustomEvent('cart:error', { message: 'Could not update cart. Please try again.' });
        });
    }

    refresh() {
      const sections = this.getSectionsToRender().map((s) => s.section).join(',');

      fetch(`${window.location.pathname}?sections=${sections}`)
        .then((response) => response.text())
        .then((state) => {
          const parsedState = JSON.parse(state);

          this.getSectionsToRender().forEach((section) => {
            const el =
              document.getElementById(section.id)?.querySelector(section.selector) ||
              document.getElementById(section.id);
            if (el) {
              el.innerHTML = this.getSectionInnerHTML(parsedState[section.section], section.selector);
            }
          });

          this.removeProductEvent();
        })
        .catch((err) => {
          console.error('Cart refresh failed:', err);
        });
    }
  }
  customElements.define('cart-drawer', CartDrawer);
}

/**
 *  @class
 *  @function LocalizationForm
 */
if (!customElements.get('localization-form')) {
  class LocalizationForm extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.inputs = this.form.querySelectorAll('[name="locale_code"], [name="country_code"]');
      this.debouncedOnSubmit = debounce(() => {
        this.onSubmitHandler();
      }, 200);
      this.inputs.forEach((item) => item.addEventListener('change', this.debouncedOnSubmit.bind(this)));
    }

    onSubmitHandler() {
      if (this.form) this.form.submit();
    }
  }
  customElements.define('localization-form', LocalizationForm);
}

/**
 *  @class
 *  @function ResizeSelect
 */
if (!customElements.get('resize-select')) {
  class ResizeSelect extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.select = this.querySelector('select');
      if (!this.select) return;
      this.addEventListeners();
    }

    addEventListeners() {
      this.addEventListener('change', this.resizeSelect.bind(this));
      window.addEventListener('load', this.resizeSelect.bind(this));
      this.resizeSelect();
    }

    resizeSelect() {
      if (!this.select || !this.select.selectedOptions.length) return;

      const tempOption = document.createElement('option');
      tempOption.textContent = this.select.selectedOptions[0].textContent;

      const tempSelect = document.createElement('select');
      const offset = 10;
      tempSelect.style.visibility = 'hidden';
      tempSelect.style.position = 'fixed';
      tempSelect.appendChild(tempOption);
      this.after(tempSelect);
      if (tempSelect.clientWidth > 0) {
        this.select.style.width = `${+tempSelect.clientWidth + offset}px`;
      }
      tempSelect.remove();
    }
  }
  customElements.define('resize-select', ResizeSelect);
}

/**
 *  @class
 *  @function QuickView
 */
if (!customElements.get('quick-view')) {
  class QuickView extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.drawer = document.getElementById('Product-Modal');
      this.body = document.body;

      this.addEventListener('click', this.setupEventListener.bind(this));
      this.addEventListener('keypress', this.setupEventListener.bind(this));
    }

    setupEventListener(e) {
      if (e.key && e.key !== 'Enter') return;
      e.preventDefault();

      const productHandle = this.dataset.productHandle;
      if (!productHandle || this.classList.contains('loading')) return;

      let href = `${theme.routes.root_url}/products/${productHandle}?view=quick-view`;
      href = href.replace('//', '/');

      this.classList.add('loading');
      fetch(href, { method: 'GET' })
        .then((response) => response.text())
        .then((text) => {
          const parsed = new DOMParser().parseFromString(text, 'text/html');
          const modalContent = parsed.querySelector('#Product-Modal-Content');

          if (!modalContent) {
            this.classList.remove('loading');
            return;
          }

          this.renderQuickview(modalContent.innerHTML, href, productHandle);
        })
        .catch((err) => {
          console.error('Quick view fetch failed:', err);
        })
        .finally(() => {
          this.classList.remove('loading');
        });
    }

    renderQuickview(sectionInnerHTML, href, productHandle) {
      if (!sectionInnerHTML || !this.drawer) return;

      const modalContent = this.drawer.querySelector('#Product-Modal-Content');
      if (!modalContent) return;

      modalContent.innerHTML = sectionInnerHTML;

      const js_files = modalContent.querySelectorAll('script');

      if (js_files.length > 0) {
        const head = document.getElementsByTagName('head')[0];
        js_files.forEach((js_file) => {
          if (!js_file.src || document.querySelector(`script[src="${js_file.src}"]`)) return;
          const script = document.createElement('script');
          script.src = js_file.src;
          head.appendChild(script);
        });
      }

      setTimeout(() => {
        if (window.Shopify?.PaymentButton) {
          window.Shopify.PaymentButton.init();
        }
        if (window.ProductModel) {
          window.ProductModel.loadShopifyXR();
        }
      }, 300);

      this.drawer.setAttribute('open', '');
      this.drawer.querySelector('.product-modal__toggle')?.focus();

      setTimeout(() => {
        const slider = this.drawer.querySelector('#Product-Slider');
        if (slider && typeof slider.reInit === 'function') {
          slider.reInit();
        }
        window.dispatchEvent(new Event('resize'));
      }, 100);

      dispatchCustomEvent('quick-view:open', { productUrl: href, productHandle });
      addIdToRecentlyViewed(productHandle);
    }
  }
  customElements.define('quick-view', QuickView);
}

/**
 *  @class
 *  @function ThemeScroll
 */
if (!customElements.get('theme-scroll')) {
  class ThemeScroll extends HTMLElement {
    constructor() {
      super();
      this.viewport = this.querySelector('.theme-scroll--inner');
      this.slideWidth = 274;
      this.slidesToScroll = 1;
    }

    connectedCallback() {
      if (window.OverlayScrollbarsGlobal?.OverlayScrollbars && this.viewport) {
        window.OverlayScrollbarsGlobal.OverlayScrollbars({
          target: this,
          elements: { viewport: this.viewport }
        }, {});
      }

      this.init();
    }

    init() {
      this.setSlides();
      this.reachedEnd = false;

      if (!this.viewport || this.slides.length < 1) return;

      this.nav = this.querySelector('.theme-scroll-nav');
      this.navPrev = this.querySelector('.flickity-prev');
      this.navNext = this.querySelector('.flickity-next');

      if (!this.nav || !this.navPrev || !this.navNext) return;

      this.resizeObserver = new ResizeObserver(() => {
        this.slideWidth = this.slides.length > 1
          ? this.slides[1].offsetLeft - this.slides[0].offsetLeft
          : this.slides[0].offsetWidth;

        this.reachedEnd =
          this.viewport.scrollWidth - this.viewport.offsetWidth === this.viewport.scrollLeft;
        this.onNavDisabled();
      }, { root: this });

      this.debouncedOnScroll = debounce(() => {
        this.onScroll();
      }, 100);

      this.setListeners();
      this.centerArrows();
    }

    setSlides() {
      this.slides = this.querySelectorAll('.carousel__slide');
      this.slidesLength = this.slides.length;
      this.lastSlide = this.slides[this.slides.length - 1];
    }

    setListeners() {
      this.nav.addEventListener('click', this.onNavClick.bind(this));
      this.viewport.addEventListener('scroll', this.debouncedOnScroll.bind(this));
      this.viewport.addEventListener('resize', this.debouncedOnScroll.bind(this));
      this.resizeObserver.observe(this.slides[0]);
    }

    onScroll() {
      this.reachedEnd =
        this.viewport.scrollWidth - this.viewport.offsetWidth === Math.floor(this.viewport.scrollLeft);
      this.onNavDisabled();
    }

    onNavClick(e) {
      if (!e.target.matches('.flickity-nav')) return;
      if (e.target.name === 'next') {
        this.scrollPos = this.viewport.scrollLeft + this.slidesToScroll * this.slideWidth;
      } else if (e.target.name === 'prev') {
        this.scrollPos = this.viewport.scrollLeft - this.slidesToScroll * this.slideWidth;
      }
      this.viewport.scrollTo({ left: this.scrollPos, behavior: 'smooth' });
    }

    onNavDisabled() {
      if (this.navPrev) this.navPrev.disabled = this.viewport.scrollLeft === 0 ? 1 : 0;
      if (this.navNext) this.navNext.disabled = this.reachedEnd ? 1 : 0;
    }

    centerArrows() {
      const first_cell = this.slides[0];
      let max_height = 0;
      let image_height = 0;

      if (!first_cell) return;

      if (first_cell.querySelector('.product-card-image')) {
        image_height = first_cell.querySelector('.product-card-image').clientHeight;
      } else if (first_cell.querySelector('.gallery--item')) {
        const gallerySlide = this.slides.length > 1 ? this.slides[1] : this.slides[0];
        image_height = gallerySlide.querySelector('.product-card-image')?.clientHeight || 0;
      }

      if (image_height > 0) {
        this.slides.forEach((item) => {
          if (item.clientHeight > max_height) max_height = item.clientHeight;
        });

        if (max_height > image_height) {
          const difference = (max_height - image_height) / -2;
          this.navPrev.style.transform = `translateY(${difference}px)`;
          this.navNext.style.transform = `translateY(${difference}px)`;
        }
      }
    }
  }
  customElements.define('theme-scroll', ThemeScroll);
}

/**
 *  @class
 *  @function CollapsibleRow
 */
if (!customElements.get('collapsible-row')) {
  class CollapsibleRow extends HTMLElement {
    constructor() {
      super();

      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.content = this.querySelector('.collapsible__content');
      this.collapsed_mobile = this.classList.contains('accordion--collapsed-mobile');

      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;
      // an ad-hoc instance property set later in setListeners.
      this.initialized = false;
    }

    connectedCallback() {
      this.setListeners();
    }

    setListeners() {
      if (this.initialized) return;

      this.querySelector('summary').addEventListener('click', (e) => this.onClick(e));
      this.initialized = true;

      if (this.collapsed_mobile) {
        window.addEventListener('resize', () => {
          this.details.open = window.innerWidth > 768;
        });
        window.dispatchEvent(new Event('resize'));
      }
    }

    onClick(e) {
      e.preventDefault();
      this.details.style.overflow = 'hidden';

      if (this.isClosing || !this.details.open) {
        this.open();
      } else if (this.isExpanding || this.details.open) {
        this.shrink();
      }
    }

    shrink() {
      this.isClosing = true;
      const startHeight = `${this.details.offsetHeight}px`;
      const endHeight = `${this.summary.offsetHeight}px`;

      if (this.animation) this.animation.cancel();

      this.animation = this.details.animate(
        { height: [startHeight, endHeight] },
        { duration: 250, easing: 'ease' }
      );
      this.animation.onfinish = () => this.onAnimationFinish(false);
      this.animation.oncancel = () => (this.isClosing = false);
    }

    open() {
      this.details.style.height = `${this.details.offsetHeight}px`;
      this.details.open = true;
      window.requestAnimationFrame(() => this.expand());
    }

    expand() {
      this.isExpanding = true;
      const startHeight = `${this.details.offsetHeight}px`;
      const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

      if (this.animation) this.animation.cancel();

      this.animation = this.details.animate(
        { height: [startHeight, endHeight] },
        { duration: 250, easing: 'ease-out' }
      );
      this.animation.onfinish = () => this.onAnimationFinish(true);
      this.animation.oncancel = () => (this.isExpanding = false);
    }

    onAnimationFinish(open) {
      this.details.open = open;
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;
      this.details.style.height = this.details.style.overflow = '';
      this.dispatchEvent(new Event('animationend', { bubbles: false }));
    }
  }
  customElements.define('collapsible-row', CollapsibleRow);
}

/**
 *  @function addIdToRecentlyViewed
 */
function addIdToRecentlyViewed(handle) {
  if (!handle) {
    const product = document.querySelector('.thb-product-detail');
    if (product) handle = product.dataset.handle;
  }
  if (!handle) return;

  try {
    if (window.localStorage) {
      const recentIds = window.localStorage.getItem('recently-viewed');
      if (recentIds) {
        const parsedIds = JSON.parse(recentIds);
        window.recentlyViewedIds = Array.isArray(parsedIds) ? parsedIds : [];
      }
    }
  } catch (e) {
    window.recentlyViewedIds = [];
  }

  const i = window.recentlyViewedIds.indexOf(handle);
  if (i > -1) window.recentlyViewedIds.splice(i, 1);

  window.recentlyViewedIds.unshift(handle);

  if (window.localStorage) {
    window.localStorage.setItem('recently-viewed', JSON.stringify(window.recentlyViewedIds));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const scrollbarWidth = window.innerWidth - document.body.clientWidth;
  document.documentElement.style.setProperty('--scrollbar-width', scrollbarWidth + 'px');
});

/* ==========================================================================
   KA Club Tooltip Logic (Global - Grid & PDP)
   ========================================================================== */

window.toggleKaTooltip = function(e, element) {
  e.preventDefault();
  e.stopPropagation();

  let tooltip = element.nextElementSibling;

  if (!tooltip || !tooltip.matches('.ka-tooltip, .ka-card-tooltip')) {
    tooltip = element.parentElement?.querySelector('.ka-tooltip, .ka-card-tooltip');
  }

  if (!tooltip) return;

  const allOpenTooltips = document.querySelectorAll('.ka-card-tooltip.is-visible, .ka-tooltip.is-visible');
  allOpenTooltips.forEach((t) => {
    if (t !== tooltip) t.classList.remove('is-visible');
  });

  tooltip.classList.toggle('is-visible');
};

document.addEventListener('click', function(e) {
  const isIcon = e.target.closest('.ka-card-info-icon') || e.target.closest('[data-ka-tooltip]');
  const isTooltip = e.target.closest('.ka-card-tooltip') || e.target.closest('.ka-tooltip');

  if (!isIcon && !isTooltip) {
    document
      .querySelectorAll('.ka-card-tooltip.is-visible, .ka-tooltip.is-visible')
      .forEach((t) => t.classList.remove('is-visible'));
  }
});
